/**
 * VLM Admin — English. The voice (brand/BRAND.md §5): calm and specific — where, when, how much —
 * state first, detail second; one word per thing (the glossary); separators are "•", never "·".
 * Units and dates are not written here: @vlm/i18n's formatters print them.
 */
export const en = {
  app: {
    name: 'VLM Admin',
    skip: 'Skip to content',
    menu: 'Menu',
    navigation: 'Main navigation',
  },

  common: {
    save: 'Save',
    saveChanges: 'Save changes',
    cancel: 'Cancel',
    close: 'Close',
    confirm: 'Confirm',
    edit: 'Edit',
    delete: 'Delete',
    back: 'Back',
    add: 'Add',
    search: 'Search',
    clearSearch: 'Clear search',
    retry: 'Try again',
    loading: 'Loading',
    refresh: 'Refresh',
    dismiss: 'Dismiss',
    actions: 'Actions',
    status: 'Status',
    all: 'All',
    anyStatus: 'Any status',
    notes: 'Notes',
    notesPlaceholder: 'Anything worth knowing',
    optional: 'optional',
    name: 'Name',
    type: 'Type',
    from: 'From',
    to: 'To',
    period: 'Period',
    view: 'View',
    more: 'More',
    never: 'Never',
    none: 'None',
    showOnMap: 'Show on map',
    required: 'Required',
    tooLong: 'At most {max} characters',
    invalidEmail: 'Enter an email address like name@company.com',
    invalidNumber: 'Enter a number',
    mustBePositive: 'Must be greater than 0',
    yearRange: 'A year between 1950 and 2100',
    range: '{from}–{to} of {total}',
    previousPage: 'Previous page',
    nextPage: 'Next page',
    rowsPerPage: 'Rows per page',
    noMatchTitle: 'Nothing matches',
    noMatchBody: 'Try another search, or clear the filters.',
    clearFilters: 'Clear filters',
    loadFailedTitle: "Couldn't load this",
    loadFailedBody: 'Check your connection and try again.',
    vehicle: 'Vehicle',
    allVehicles: 'All vehicles',
    chooseVehicle: 'Choose a vehicle',
    searchVehicles: 'Search vehicles',
    noVehicleMatch: 'No vehicle matches',
    selected: 'Selected',
    activate: 'Activate',
    deactivate: 'Deactivate',
  },

  units: {
    km: 'km',
    kmh: 'km/h',
  },

  presets: {
    today: 'Today',
    yesterday: 'Yesterday',
    last7: 'Last 7 days',
    last30: 'Last 30 days',
    thisMonth: 'This month',
    custom: 'Custom',
  },

  nav: {
    live: 'Live map',
    vehicles: 'Vehicles',
    devices: 'Tracking devices',
    assignments: 'Assignments',
    trips: 'Trips',
    geofences: 'Geofences',
    alerts: 'Alerts',
    reports: 'Reports',
    users: 'Users',
    alertSettings: 'Alert settings',
    billing: 'Billing',
    profile: 'Your profile',
  },

  shell: {
    theme: 'Theme',
    themeDay: 'Day',
    themeNight: 'Night',
    themeAuto: 'Match the system',
    language: 'Language',
    switchLanguage: 'العربية',
    switchLanguageLabel: 'Switch to Arabic',
    signOut: 'Sign out',
    account: 'Account',
    liveOn: 'Live',
    liveConnecting: 'Connecting',
    liveReconnecting: 'Reconnecting',
    liveOff: 'Not live',
    liveOnHint: 'Positions and alerts arrive as vehicles report.',
    liveDownHint: 'The live feed dropped and is reconnecting. The map still refreshes every 30 seconds.',
    paymentOverdue: 'Payment overdue',
    paymentFirst: 'Awaiting payment',
    paymentOverdueHint: '{amount} was due on {date}. The bank details are on the Billing page.',
    paymentFirstHint: 'Your plan starts with its first payment, {amount}. The bank details are on the Billing page.',
    trial: 'Free trial',
    trialChip: { one: 'Trial • last day', other: 'Trial • {count} days left' },
    trialHint: 'Everything is unlimited until the end of {date}. Choose a plan with Viganium before then to keep signing in.',
    trialHintPlan: 'Everything is unlimited until the end of {date}; your plan starts the day after.',
    expandMenu: 'Expand menu',
    collapseMenu: 'Collapse menu',
  },

  notifications: {
    title: 'Notifications',
    open: 'Notifications',
    unread: { one: '{count} unread', other: '{count} unread' },
    unreadLabel: { one: '{count} unread notification', other: '{count} unread notifications' },
    empty: 'No notifications',
    emptyBody: 'Alerts for your fleet land here as they happen.',
    markAllRead: 'Mark all as read',
    viewAlerts: 'All alerts',
  },

  auth: {
    title: 'Sign in',
    subtitle: 'VLM Admin • Viganium Fleet Management',
    username: 'Username',
    password: 'Password',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    submit: 'Sign in',
    usernameRequired: 'Enter your username',
    passwordRequired: 'Enter your password',
    expired: 'Your session ended. Sign in again.',
    checking: 'Signing you in',
    unreachableTitle: "Can't reach VLM",
    unreachableBody: 'The API is not answering. Check your connection; your sign-in is kept.',
    newCompany: 'New to VLM?',
    register: 'Register your company',
    // the words on the sign-in picture's map (RouteScene, shown in capitals): a trip from a depot to the pin
    scene: {
      lat: '21°25′N',
      lng: '39°49′E',
      scale: 'Zoom 14 • 1:34,000',
      from: 'From Depot 3 • 08:02',
      eta: 'ETA 10:15',
    },
  },

  roles: {
    ADMIN: 'Admin',
    OPERATOR: 'Operator',
    VIEWER: 'Viewer',
  },

  roleHints: {
    ADMIN: 'Everything, including users, alert settings and billing.',
    OPERATOR: 'Vehicles, tracking devices, assignments, geofences and alerts.',
    VIEWER: 'Sees the live map, trips, alerts and reports.',
  },

  status: {
    ACTIVE: 'Active',
    INACTIVE: 'Inactive',
  },

  tracking: {
    MOVING: 'Moving',
    STOPPED: 'Stopped',
    OFFLINE: 'Offline',
  },

  vehicleTypes: {
    CAR: 'Car',
    TRUCK: 'Truck',
    BUS: 'Bus',
  },

  alertTypes: {
    OVERSPEED: 'Overspeed',
    GEOFENCE_ENTER: 'Entered a geofence',
    GEOFENCE_EXIT: 'Left a geofence',
    IDLE_TIME: 'Idling',
    HARSH_DRIVING: 'Harsh driving',
  },

  alertStatus: {
    NEW: 'New',
    ACKNOWLEDGED: 'Acknowledged',
    RESOLVED: 'Resolved',
  },

  geofenceTypes: {
    RADIUS: 'Circle',
    POLYGON: 'Polygon',
  },

  directions: {
    N: 'N',
    NE: 'NE',
    E: 'E',
    SE: 'SE',
    S: 'S',
    SW: 'SW',
    W: 'W',
    NW: 'NW',
  },

  live: {
    title: 'Live map',
    vehicles: 'Vehicles',
    search: 'Search vehicles or plates',
    noVehicles: 'No vehicles on the map yet',
    noVehiclesBody: 'A vehicle appears once a tracking device is linked to it and reports.',
    noMatch: 'No vehicle matches',
    noPosition: 'No position yet',
    lastReport: 'Last report',
    speed: 'Speed',
    heading: 'Heading',
    device: 'Tracking device',
    position: 'Position',
    reported: 'Reported {ago}',
    trips: 'Trips',
    todaysTrack: "Today's track",
    follow: 'Follow',
    following: 'Following',
    zoomTo: 'Zoom to vehicle',
    closeDetails: 'Close details',
    fitAll: 'Show the whole fleet',
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    satellite: 'Satellite view',
    showGeofences: 'Show geofences',
    hideGeofences: 'Hide geofences',
    filter: 'Filter by status',
    newAlert: '{type} — {vehicle}',
  },

  mapSearch: {
    label: 'Search the map',
    placeholder: 'Search a place or coordinates',
    coordinates: 'Coordinates',
    searching: 'Searching…',
    none: 'No places found',
    failed: 'Place search is not available right now',
    found: { zero: 'No places found', one: '{count} place found', other: '{count} places found' },
  },

  vehicles: {
    title: 'Vehicles',
    description: 'Every vehicle in the fleet and the tracking device it carries.',
    add: 'Add vehicle',
    edit: 'Edit vehicle',
    name: 'Vehicle name',
    plate: 'Plate number',
    type: 'Type',
    anyType: 'Any type',
    model: 'Model',
    year: 'Year',
    color: 'Colour',
    device: 'Tracking device',
    noDevice: 'No tracking device',
    search: 'Search by name or plate',
    created: '{name} added',
    saved: '{name} saved',
    activateTitle: 'Activate {name}?',
    activateBody: 'It returns to the live map once its tracking device reports.',
    deactivateTitle: 'Deactivate {name}?',
    deactivateBody: 'It leaves the live map and stops raising alerts. Its trips and history stay.',
    activated: '{name} is active',
    deactivated: '{name} is inactive',
    lastPosition: 'Last position',
    lastPositionTitle: '{name} • last position',
    noPosition: 'No position for this vehicle yet',
    noPositionBody: 'It appears once a linked tracking device reports.',
    empty: 'No vehicles yet',
    emptyBody: 'Add the first vehicle, then link a tracking device to it.',
    notFound: 'This vehicle does not exist',
  },

  devices: {
    title: 'Tracking devices',
    description: 'The boxes in the vehicles: IMEI, SIM card, network and the vehicle each one is in.',
    add: 'Add tracking device',
    edit: 'Edit tracking device',
    name: 'Device name',
    imei: 'IMEI',
    imeiLocked: 'The IMEI identifies the device to the gateway; it cannot change.',
    sim: 'SIM number',
    provider: 'Network',
    anyProvider: 'Any network',
    otherProvider: 'Other',
    chooseProvider: 'Choose a network',
    model: 'Model',
    vehicle: 'Vehicle',
    notLinked: 'Not in a vehicle',
    search: 'Search by name or IMEI',
    created: '{name} added',
    saved: '{name} saved',
    activateTitle: 'Activate {name}?',
    activateBody: 'Its reports count again.',
    deactivateTitle: 'Deactivate {name}?',
    deactivateBody: 'Its reports stop counting. Unlink it from its vehicle first if it is being replaced.',
    activated: '{name} is active',
    deactivated: '{name} is inactive',
    empty: 'No tracking devices yet',
    emptyBody: 'Add a device with its IMEI, then link it to a vehicle.',
    notFound: 'This tracking device does not exist',
  },

  assignments: {
    title: 'Assignments',
    description: 'Which tracking device is in which vehicle. Unlinking stops tracking that vehicle.',
    link: 'Link a device',
    linkTitle: 'Link a tracking device to a vehicle',
    linkHint: 'The link starts today. Only active vehicles without a device, and active devices not in a vehicle, are offered.',
    vehicle: 'Vehicle',
    device: 'Tracking device',
    chooseDevice: 'Choose a tracking device',
    searchDevices: 'Search devices or IMEIs',
    noDeviceMatch: 'No device matches',
    linkedOn: 'Linked on',
    noFreeVehicles: 'Every active vehicle already has a tracking device.',
    noFreeDevices: 'Every active tracking device is already in a vehicle.',
    linked: '{device} is now in {vehicle}',
    unlink: 'Unlink',
    unlinkTitle: 'Unlink {device}?',
    unlinkBody: '{vehicle} stops being tracked until another device is linked to it.',
    unlinked: '{device} unlinked from {vehicle}',
    linkedState: 'Linked',
    unlinkedState: 'Unlinked',
    search: 'Search by IMEI or plate',
    empty: 'No assignments yet',
    emptyBody: 'Link a tracking device to a vehicle to put it on the map.',
  },

  trips: {
    title: 'Trips',
    description: 'Every trip the fleet has driven: when, how far, how fast.',
    trip: 'Trip',
    start: 'Start',
    end: 'End',
    duration: 'Duration',
    distance: 'Distance',
    avgSpeed: 'Avg speed',
    maxSpeed: 'Top speed',
    inProgress: 'In progress',
    inProgressHint: 'Still driving — this trip has not ended yet.',
    openTrip: 'Open trip {id}',
    empty: 'No trips in this period',
    emptyBody: 'Trips appear here the moment a vehicle starts moving.',
  },

  trip: {
    title: 'Trip {id}',
    allTrips: 'All trips',
    route: 'Route',
    timeline: 'Timeline',
    started: 'Trip started',
    ended: 'Trip ended',
    stillDriving: 'Still driving',
    stop: 'Stop',
    stopFor: 'Stopped for {duration}',
    stopOngoing: 'Stopped since {time}',
    stops: { zero: 'No stops', one: '{count} stop', other: '{count} stops' },
    noRoute: 'No route points were recorded for this trip.',
    dayTrack: 'The whole day on the map',
    notFound: 'This trip does not exist',
  },

  geofences: {
    title: 'Geofences',
    description: 'Areas on the map. A vehicle entering or leaving one raises an alert.',
    add: 'Draw a geofence',
    edit: 'Edit geofence',
    newTitle: 'New geofence',
    name: 'Name',
    namePlaceholder: 'Depot 3, Al Noor Market…',
    shape: 'Shape',
    radius: 'Radius',
    radiusUnit: 'metres',
    center: 'Centre',
    latitude: 'Latitude',
    longitude: 'Longitude',
    corners: 'Corners',
    cornerCount: { zero: 'No corners yet', one: '{count} corner', other: '{count} corners' },
    circleHint: 'Search for a place or click the map to place the centre; drag it to move it.',
    polygonHint: 'Click the map to add corners — at least 3. Drag a corner to move it.',
    clearCorners: 'Clear corners',
    removeCorner: 'Remove corner {n}',
    undoCorner: 'Remove the last corner',
    size: 'Size',
    created: 'Geofence {name} saved',
    saved: 'Geofence {name} saved',
    deleted: 'Geofence {name} deleted',
    deleteTitle: 'Delete {name}?',
    deleteBody: 'If vehicles have entered or left it, it is kept as inactive so their history stays.',
    needCenter: 'Place the centre on the map.',
    needRadius: 'Set a radius greater than 0.',
    needCorners: 'Add at least 3 corners.',
    latRange: 'Between −90 and 90',
    lngRange: 'Between −180 and 180',
    map: 'Map',
    createdOn: 'Created',
    empty: 'No geofences yet',
    emptyBody: 'Draw an area — a depot, a customer site — to be told when vehicles come and go.',
    notFound: 'This geofence does not exist',
  },

  alerts: {
    title: 'Alerts',
    description: 'Speeding, harsh driving, idling and geofence crossings across the fleet.',
    alert: 'Alert {id}',
    type: 'Type',
    anyType: 'Any type',
    time: 'Time',
    speed: 'Speed',
    what: 'What happened',
    location: 'Location',
    noLocation: 'No position recorded',
    acknowledge: 'Acknowledge',
    resolve: 'Resolve',
    acknowledged: 'Alert {id} acknowledged',
    resolved: 'Alert {id} resolved',
    nextStep: 'Next: {status}',
    done: 'Resolved — nothing left to do.',
    empty: 'No alerts in this period',
    emptyBody: 'Quiet roads. Alerts appear here as they happen.',
    notFound: 'This alert does not exist',
  },

  alertSettings: {
    title: 'Alert settings',
    description: 'Which alerts VLM raises, at what threshold, and who gets them by email.',
    raise: 'Raise this alert',
    speedLimit: 'Speed limit',
    speedThreshold: 'Speed change',
    idleTime: 'Idle time',
    kmh: 'km/h',
    kmhPerSecond: 'km/h per s',
    minutes: 'min',
    email: 'Send by email',
    emailRecipients: 'Email addresses',
    emailHint: 'Separate addresses with commas, up to {max}. Every alert of this type emails each of them.',
    needAddress: 'Enter at least one email address.',
    badAddress: '{address} is not an email address.',
    maxAddresses: 'Up to {max} addresses.',
    needThreshold: 'Set a value greater than 0.',
    cooldown: 'Quiet period',
    needCooldown: 'Enter whole minutes, from 0 to 1440.',
    cooldownHint: {
      OVERSPEED: 'One alert per episode: a new one only after the vehicle has stayed under the limit this long. 0 alerts on every report over it.',
      HARSH_DRIVING: 'No new alert for the same vehicle this long after the last one. 0 alerts on every hard brake or acceleration.',
    },
    saved: 'Alert settings saved',
    unsaved: 'Unsaved changes',
    discard: 'Discard changes',
    about: {
      OVERSPEED: 'When a vehicle drives faster than the speed limit.',
      GEOFENCE_ENTER: 'When a vehicle enters a geofence.',
      GEOFENCE_EXIT: 'When a vehicle leaves a geofence.',
      IDLE_TIME: 'When a vehicle stands still with its engine on for longer than this. Needs a tracking device that reports the ignition.',
      HARSH_DRIVING: 'When a vehicle’s speed changes by more than this in one second — hard braking or hard acceleration.',
    },
  },

  /** An alert in words, from the values the API stores with it (params). */
  alertText: {
    OVERSPEED: '{vehicle} went over the speed limit: {speed} (limit {limit}).',
    HARSH_DRIVING: '{vehicle} changed speed by {change} — hard braking or acceleration.',
    GEOFENCE_ENTER: '{vehicle} entered the geofence {geofence}.',
    GEOFENCE_EXIT: '{vehicle} left the geofence {geofence}.',
    IDLE_TIME: '{vehicle} has stood still with the engine on for {duration} (limit {limit}).',
  },

  users: {
    title: 'Users',
    description: 'Who in your company can sign in to VLM Admin, and what each of them can do.',
    add: 'Add user',
    edit: 'Edit user',
    username: 'Username',
    usernameLocked: 'A username cannot change.',
    fullName: 'Full name',
    email: 'Email',
    phone: 'Phone',
    password: 'Password',
    passwordHint: 'At least 8 characters.',
    passwordShort: 'At least 8 characters',
    role: 'Role',
    anyRole: 'Any role',
    language: 'Language',
    lastLogin: 'Last sign-in',
    search: 'Search by username or name',
    you: 'You',
    created: '{name} added',
    saved: '{name} saved',
    activateTitle: 'Activate {name}?',
    activateBody: 'They can sign in again.',
    deactivateTitle: 'Deactivate {name}?',
    deactivateBody: 'They cannot sign in until they are activated again.',
    activated: '{name} is active',
    deactivated: '{name} is inactive',
    empty: 'No users found',
    emptyBody: 'Add the people who run the fleet.',
    notFound: 'This user does not exist',
  },

  profile: {
    title: 'Your profile',
    description: 'Your details, your password and how VLM looks for you.',
    details: 'Details',
    account: 'Account',
    photo: 'Photo',
    changePhoto: 'Change photo',
    uploadPhoto: 'Upload',
    removePhoto: 'Remove photo',
    removePhotoTitle: 'Remove your photo?',
    removePhotoBody: 'Your initials show instead.',
    photoRules: 'JPEG, PNG or WebP, up to 5 MB.',
    photoType: 'That file is not a JPEG, PNG or WebP image.',
    photoSize: 'That image is over 5 MB.',
    photoUpdated: 'Photo updated',
    photoRemoved: 'Photo removed',
    saved: 'Profile saved',
    password: 'Password',
    currentPassword: 'Current password',
    newPassword: 'New password',
    confirmPassword: 'Repeat the new password',
    mismatch: 'The two passwords do not match.',
    changePassword: 'Change password',
    passwordChanged: 'Password changed',
    preferences: 'Preferences',
    preferencesHint: 'Saved on this browser; the language is also saved to your account.',
  },

  reports: {
    title: 'Reports',
    description: 'Trips, activity, alerts and geofence visits for any period — on screen, or as CSV, Excel or PDF.',
    open: 'Open',
    run: 'Run report',
    export: 'Export',
    exportAs: 'Export as {format}',
    exporting: 'Preparing the {format} file',
    exported: '{file} downloaded',
    exportFailed: 'The export failed. Try again.',
    needRange: 'Choose a period.',
    badRange: 'The end must be after the start.',
    needVehicle: 'Choose a vehicle.',
    rows: { zero: 'No rows', one: '{count} row', other: '{count} rows' },
    emptyTitle: 'Nothing in this period',
    emptyBody: 'Try a longer period, or another vehicle.',
    prompt: 'Choose a period, then run the report.',
    total: 'Total',
    cards: {
      trips: { title: 'Trips', body: 'Each finished trip with its distance, duration and average speed.' },
      activity: { title: 'Vehicle activity', body: 'Per vehicle: distance, trips, driving, idling and stopped time, top speed.' },
      track: { title: 'Track explorer', body: 'Every position a vehicle reported in a time range — on the map and as a table.' },
      alerts: { title: 'Alerts', body: 'Alert records, with a breakdown by vehicle and by type.' },
      geofences: { title: 'Geofence visits', body: 'When vehicles entered and left geofences, and how long they stayed.' },
    },
    tripCount: 'Trips',
    totalDistance: 'Distance',
    drivingTime: 'Driving time',
    averageSpeed: 'Average speed',
    activity: {
      distance: 'Distance',
      trips: 'Trips',
      driving: 'Driving',
      idling: 'Idling',
      stopped: 'Stopped',
      topSpeed: 'Top speed',
    },
    track: {
      positions: { zero: 'No positions', one: '{count} position', other: '{count} positions' },
      positionsLabel: 'Positions',
      noPositions: 'No positions in this range',
      noPositionsBody: 'The vehicle did not report between these times. Try a wider range.',
      mapTab: 'Map',
      tableTab: 'Table',
      start: 'Start',
      end: 'End',
      time: 'Time',
      latitude: 'Latitude',
      longitude: 'Longitude',
      speed: 'Speed',
      heading: 'Heading',
      distance: 'Distance driven',
      topSpeed: 'Top speed',
      legend: 'Speed',
      bandStopped: 'Stopped or unknown',
      bandSlow: 'Under 60',
      bandMid: '60 to 80',
      bandFast: '80 to 120',
      bandTooFast: '120 and over',
      showPoint: 'Show position {n} on the map',
    },
    alerts: {
      perVehicle: 'Alerts per vehicle',
      byType: 'Alerts by type',
      records: 'Alert records',
      count: 'Alerts',
      locationTitle: 'Alert location',
    },
    geofences: {
      geofence: 'Geofence',
      allGeofences: 'All geofences',
      entered: 'Entered',
      left: 'Left',
      inside: 'Time inside',
      stillInside: 'Still inside',
    },
  },

  errors: {
    network: "Can't reach VLM. Check your connection and try again.",
    server: 'VLM could not do that just now ({status}). Try again.',
    unknown: 'Something went wrong. Try again.',
    accessDenied: 'Your role does not allow this.',
    notFound: 'It no longer exists — someone may have removed it.',
    invalidCredentials: 'That username and password do not match.',
    deactivated: 'This account is deactivated. Ask an administrator to activate it.',
    lastAdmin: 'You cannot deactivate the last active admin.',
    wrongPassword: 'The current password is wrong.',
    vehicleHasDevice: 'This vehicle already has a tracking device. Unlink it first.',
    deviceInUse: 'This tracking device is already in another vehicle. Unlink it first.',
    duplicatePlate: 'A vehicle with plate {value} already exists.',
    duplicateImei: 'A tracking device with IMEI {value} already exists.',
    duplicateUsername: 'The username {value} is taken.',
    polygonCorners: 'A polygon needs at least 3 corners.',
    circleCenter: 'A circle needs a centre.',
    circleRadius: 'The radius must be greater than 0.',
    companyPending: "Your company's registration is waiting for Viganium's approval.",
    companySuspended: "Your company's account is suspended. Contact Viganium to restore it.",
    companyRejected: "Your company's registration was not approved. Contact Viganium for details.",
    companyInactive: "Your company's account is not active. Contact Viganium.",
    companyTrialEnded: "Your company's free trial has ended. Contact Viganium to choose a plan — your data is kept.",
    planVehicles: {
      one: 'Your plan allows {count} active vehicle. Deactivate it first, or ask Viganium for a bigger plan.',
      other: 'Your plan allows up to {count} active vehicles. Deactivate one first, or ask Viganium for a bigger plan.',
    },
    planUsers: {
      one: 'Your plan allows {count} active user. Deactivate them first, or ask Viganium for a bigger plan.',
      other: 'Your plan allows up to {count} active users. Deactivate one first, or ask Viganium for a bigger plan.',
    },
    validation: 'Some fields need another look.',
  },

  /** One field the API turned down (ErrorResponse.errors): "{field}: {problem}". */
  validation: {
    field: '{field}: {problem}',
    NotNull: 'required',
    NotBlank: 'required',
    NotEmpty: 'required',
    Size: 'between {min} and {max} characters',
    SizeMax: 'at most {max} characters',
    SizeMin: 'at least {min}',
    Email: 'not a valid email address',
    Pattern: 'not in the expected form',
    Min: 'at least {value}',
    Max: 'at most {value}',
    Positive: 'must be greater than 0',
    invalid: 'not accepted',
  },

  billing: {
    title: 'Billing',
    description: 'Your plan, the next payment and your invoices.',
    plan: 'Plan',
    noPlan: 'No plan yet',
    noPlanBody: 'Viganium assigns your plan. Get in touch to choose one.',
    trial: 'Free trial',
    trialLeft: { one: 'Last day', other: '{count} days left' },
    trialUntil: 'Until the end of {date}',
    trialBody: 'Everything is unlimited during your free trial: any number of vehicles and users.',
    trialNoPlan: 'Choose a plan with Viganium before it ends: after it, nobody in your company can sign in until it has one. Your vehicles and their history are kept.',
    trialThenPlan: 'Your plan starts the day after it ends.',
    price: { MONTHLY: '{price} a month', YEARLY: '{price} a year' },
    beforeVat: 'Before VAT • {rate}% is added on the invoice',
    paidUntil: 'Paid until',
    notPaid: 'Not paid yet',
    usage: 'Plan usage',
    vehicles: 'Active vehicles',
    users: 'Active users',
    ofLimit: '{used} of {limit}',
    unlimited: '{used} • no limit',
    next: 'Next payment',
    dueOn: 'Due on {date}',
    overdueSince: 'Overdue since {date}',
    invoice: 'Invoice {number}',
    expected: 'Not invoiced yet: what the next invoice will be.',
    nothingDue: 'Nothing to pay right now.',
    transfer: 'Pay by bank transfer',
    bank: 'Bank',
    accountName: 'Account name',
    iban: 'IBAN',
    swift: 'SWIFT',
    reference: 'Reference',
    referenceHint: 'Write your company code as the transfer reference, so the payment finds you.',
    afterTransfer: 'Viganium activates or extends your plan as soon as the transfer arrives.',
    receipt: 'Send the transfer receipt to {email}.',
    noBank: 'Ask Viganium for the bank details.',
    copy: 'Copy {label}',
    copied: '{label} copied',
    invoices: 'Invoices',
    invoicesEmpty: 'No invoices yet',
    invoicesEmptyBody: 'Your invoices appear here as soon as Viganium issues them.',
    number: 'Invoice',
    period: 'Period',
    issued: 'Issued',
    due: 'Due',
    total: 'Total',
    download: 'Download {number}',
    pdfEn: 'PDF in English',
    pdfAr: 'PDF in Arabic',
    downloaded: '{file} downloaded',
    downloadFailed: 'The invoice could not be downloaded. Try again.',
    details: 'Billing details',
    detailsHint: 'What your next invoices say about your company.',
    edit: 'Edit billing details',
    name: 'Company name',
    nameHint: 'To change the company name, contact Viganium.',
    code: 'Company code',
    contactName: 'Contact person',
    email: 'Billing email',
    emailHint: 'Invoices, receipts and payment reminders are emailed here.',
    emailHintNone: 'Until you add one, invoices, receipts and payment reminders are emailed to your company’s admins.',
    phone: 'Phone',
    address: 'Address',
    city: 'City',
    country: 'Country',
    taxNumber: 'VAT number',
    commercialRegistration: 'Commercial registration',
    invoiceLanguage: 'Invoice language',
    notSet: 'Not set',
    saved: 'Billing details saved',
  },

  /** The plan's state — the platform's words, one word per thing. */
  subscriptionStatus: {
    NONE: 'No plan',
    PENDING: 'Awaiting payment',
    ACTIVE: 'Paid',
    PAST_DUE: 'Overdue',
    CANCELED: 'Canceled',
  },

  invoiceStatus: {
    UNPAID: 'Unpaid',
    PAID: 'Paid',
    VOID: 'Void',
    OVERDUE: 'Overdue',
  },

  cycles: {
    MONTHLY: 'Monthly',
    YEARLY: 'Yearly',
  },

  pages: {
    notFoundTitle: 'This page is not on the map',
    notFoundBody: 'The address may be old, or the page has moved.',
    goLive: 'Go to the live map',
    forbiddenTitle: 'No access',
    forbiddenBody: 'Your role does not include this page. Ask an administrator if you need it.',
  },
} as const;

export type AdminMessages = typeof en;
